import { createAsyncThunk } from '@reduxjs/toolkit'
import ACTIONS from '../../constants'
import { moduleAPI } from '../../../shared/api/ModuleAPI'
import { runAPI } from '../../../shared/api/RunAPI'
import { gapAPI } from '../../../shared/api/GapAPI'
import { gapDescriptionAPI } from '../../../shared/api/GapDescriptionAPI'
import { AppState } from '../../store'
import {
  ContextModel,
  ContextModelPost,
  DescriptiveCharacteristicPost,
  DetailedGapDescription,
  DiversityVariantPostData,
  IndependentCharacteristicPost,
} from '../../../shared/model/models'
import { configurationAPI } from '../../../shared/api/ConfigurationAPI'
import { characteristicAPI } from '../../../shared/api/CharacteristicAPI'
import { variableAPI } from '../../../shared/api/VariableAPI'
import { diversityVariantAPI } from '../../../shared/api/DiversityVariantAPI'
import { contextModelAPI } from '../../../shared/api/ContextModelAPI'
import { empiricalConceptAPI } from '../../../shared/api/EmpiricalConceptAPI'
import { ofaAPI } from '../../../shared/api/OFAAPI'
import { etrlAPI } from '../../../shared/api/ETRLAPI'
import { Module } from '../../../shared/model'

const { ANALYSIS } = ACTIONS

export const setModules = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.SET_MODULES,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) return await moduleAPI.getModules()
    return null
  },
)

export const setModulesSummary = createAsyncThunk<any, { date: Date }, { state: AppState }>(
  ANALYSIS.SET_MODULES_SUMMARY,
  async ({ date }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) return await moduleAPI.getModulesSummary(date)
    return null
  },
)

export const setModulesInfo = createAsyncThunk<any, { date: Date }, { state: AppState }>(
  ANALYSIS.SET_MODULES_INFO,
  async ({ date }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) return await moduleAPI.getModulesInfo(date)
    return null
  },
)

export const setRuns = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.SET_RUNS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await runAPI.getRuns()
    }
    return null
  },
)

export const setContextVariantGaps = createAsyncThunk<
  any,
  { contextVariantId: number; startDate: Date; endDate: Date },
  { state: AppState }
>(
  ANALYSIS.SET_CONTEXT_VARIANT_GAPS,
  async ({ contextVariantId, startDate, endDate }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      const gaps = await gapAPI.getGaps(contextVariantId, startDate, endDate)
      return {
        contextVariantId,
        gaps,
      }
    }
    return null
  },
)

export const setContextVariantGapLatest = createAsyncThunk<
  any,
  { contextVariantId: number; date: Date },
  { state: AppState }
>(ANALYSIS.SET_CONTEXT_VARIANT_GAP_LATEST, async ({ contextVariantId, date }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    const latestGap = await gapAPI.getLatestGap(contextVariantId, date)
    return {
      contextVariantId,
      latestGap,
    }
  }
  return null
})

export const setModuleGapsLatest = createAsyncThunk<any, { moduleId: number }, { state: AppState }>(
  ANALYSIS.SET_MODULE_GAPS_LATEST,
  async ({ moduleId }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await gapAPI.latestGaps(moduleId)
    }
    return null
  },
)

export const setDetailedGapDescription = createAsyncThunk<
  any,
  { gapId: number; empiricalConceptId: number },
  { state: AppState }
>(ANALYSIS.SET_DETAILED_GAP_DESCRPTION, async ({ gapId, empiricalConceptId }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    const description = await gapDescriptionAPI.gapDetailedDescription(gapId, empiricalConceptId)
    description.referenceGroup.descriptions.sort(
      (a, b) => parseInt(a.descriptiveName.slice(1), 10) - parseInt(b.descriptiveName.slice(1), 10),
    )
    description.groupOfInterest.descriptions.sort(
      (a, b) => parseInt(a.descriptiveName.slice(1), 10) - parseInt(b.descriptiveName.slice(1), 10),
    )
    return {
      id: `${gapId}_${empiricalConceptId}`,
      description,
    }
  }
  return null
})

export const setConfiguration = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.SET_CONFIGURATION,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await configurationAPI.getConfiguration()
    }
    return null
  },
)

export const setCharacteristics = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.SET_CHARACTERISTICS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await characteristicAPI.getCharacteristics()
    }
    return null
  },
)

export const setInitializeVariables = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.INITIALIZE_VARIABLES,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await variableAPI.initVariables()
    }
    return null
  },
)

export const setDiversityVariants = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.SET_DIVERSITY_VARIANTS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await diversityVariantAPI.getDiversityVariants()
    }
    return null
  },
)

export const saveDiversityVariant = createAsyncThunk<
  any,
  { data: DiversityVariantPostData[] },
  { state: AppState }
>(ANALYSIS.SAVE_DIVERSITY_VARIANT, async ({ data }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    return await diversityVariantAPI.saveDiversityVariant(data)
  }
  return null
})

export const setContextModels = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.SET_CONTEXT_MODELS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      return await contextModelAPI.getContextModels()
    }
    return null
  },
)

export const saveContextModels = createAsyncThunk<
  any,
  { data: ContextModelPost[] },
  { state: AppState }
>(ANALYSIS.SAVE_DIVERSITY_VARIANT, async ({ data }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    return await contextModelAPI.saveContextModels(data)
  }
  return null
})

export const setContextVariantsInfo = createAsyncThunk<
  any,
  { moduleId: number; date: Date },
  { state: AppState }
>(ANALYSIS.SET_CONTEXT_VARIANTS_INFO, async ({ moduleId, date }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    const info = await contextModelAPI.getContextVariantsInfo(moduleId, date)
    return {
      moduleId,
      info,
    }
  }
  return null
})

export const setEmpiricalConceptConfigsForIndependentCharacteristics = createAsyncThunk<
  any,
  { contextModelId: number },
  { state: AppState }
>(
  ANALYSIS.SET_EMPIRICAL_CONCEPT_CONFIGS_FOR_INDEPENDENT_CHARACTERISTICS,
  async ({ contextModelId }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      const data = await configurationAPI.empiricalConceptConfigForIndependentCharacteristics(
        contextModelId,
      )
      return {
        contextModelId,
        data,
      }
    }
    return null
  },
)

export const setEmpiricalConceptConfigsForDescriptiveCharacteristics = createAsyncThunk<
  any,
  { contextModelId: number },
  { state: AppState }
>(
  ANALYSIS.SET_EMPIRICAL_CONCEPT_CONFIGS_FOR_DESCRIPTIVE_CHARACTERISTICS,
  async ({ contextModelId }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      const data = await configurationAPI.empiricalConceptConfigForDescriptiveCharacteristics(
        contextModelId,
      )
      return {
        contextModelId,
        data,
      }
    }
    return null
  },
)

export const setEmpiricalConceptsInfo = createAsyncThunk<
  any,
  { contextVariantId: number; gapId: number },
  { state: AppState }
>(ANALYSIS.SET_EMPIRICAL_CONCEPTS_INFO, async ({ contextVariantId, gapId }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    const info = await empiricalConceptAPI.getEmpiricalConceptsInfo(gapId)
    let newInfo: any = {}
    const key = Object.keys(info)[0]
    Object.values(info[key]).forEach((ec: DetailedGapDescription) => {
      ec.referenceGroup.descriptions.sort(
        (a, b) =>
          parseInt(a.descriptiveName.slice(1), 10) - parseInt(b.descriptiveName.slice(1), 10),
      )
      ec.groupOfInterest.descriptions.sort(
        (a, b) =>
          parseInt(a.descriptiveName.slice(1), 10) - parseInt(b.descriptiveName.slice(1), 10),
      )
      const totalGOI = Math.round(
        ec.groupOfInterest.descriptions.reduce(
          (prev: any, curr: any) => prev + +curr.descriptionDependentVariable.numberOfObservations,
          0,
        ),
      )
      const totalRG = Math.round(
        ec.referenceGroup.descriptions.reduce(
          (prev: any, curr: any) => prev + +curr.descriptionDependentVariable.numberOfObservations,
          0,
        ),
      )
      const groupDataItems = ec.referenceGroup.descriptions.map((detailedDescription, index) => ({
        referenceGroupName: ec.referenceGroup.groupVariable.label,
        groupOfInterestName: ec.groupOfInterest.groupVariable.label,
        descriptiveName: detailedDescription.descriptiveVariable.label,
        referenceGroupCount: detailedDescription.descriptionDependentVariable.numberOfObservations,
        groupOfInterestCount:
          ec.groupOfInterest.descriptions[index].descriptionDependentVariable.numberOfObservations,
        dependentCharacteristicName:
          detailedDescription.descriptionDependentVariable.characteristic.name,
        groupVariable: ec.referenceGroup.groupVariable,
        shareOfGOI: Number(
          (ec.groupOfInterest.descriptions[index].descriptionDependentVariable
            .numberOfObservations *
            100) /
            (+ec.groupOfInterest.descriptions[index].descriptionDependentVariable
              .numberOfObservations +
              +detailedDescription.descriptionDependentVariable.numberOfObservations),
        ),
        shareOfRG: Number(
          (detailedDescription.descriptionDependentVariable.numberOfObservations * 100) /
            (+ec.groupOfInterest.descriptions[index].descriptionDependentVariable
              .numberOfObservations +
              +detailedDescription.descriptionDependentVariable.numberOfObservations),
        ),
        shareFullGOI: Number(
          (ec.groupOfInterest.descriptions[index].descriptionDependentVariable
            .numberOfObservations *
            100) /
            totalGOI,
        ).toFixed(1),
        shareFullRG: Number(
          (detailedDescription.descriptionDependentVariable.numberOfObservations * 100) / totalRG,
        ).toFixed(1),
        goiMean: ec.groupOfInterest.descriptions[index].descriptionDependentVariable.mean,
        rgMean: detailedDescription.descriptionDependentVariable.mean,
      }))
      newInfo = {
        ...newInfo,
        [ec.referenceGroup.descriptiveEmpiricalConcept.id]: {
          groupDataItems,
          totalGOI,
          totalRG,
          descriptiveCharacteristicName: ec.referenceGroup.descriptiveCharacteristic.name,
          descriptiveEmpiricalConceptId: ec.referenceGroup.descriptiveEmpiricalConcept.id,
          gapCauseDescriptions: ec.gapCauseDescriptions,
        },
      }
    })

    return {
      contextVariantId,
      info: newInfo,
    }
  }
  return null
})

export const saveIndependentCharacteristicsForContextModel = createAsyncThunk<
  any,
  { contextModel: ContextModel; data: IndependentCharacteristicPost[] },
  { state: AppState }
>(ANALYSIS.SAVE_INDEPENDENT_CHARACTERISTICS, async ({ contextModel, data }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    return await contextModelAPI.saveIndependentCharacteristicsForContextModel(contextModel, data)
  }
  return null
})

export const saveIndependentCharacteristicsConstantForContextModel = createAsyncThunk<
  any,
  void,
  { state: AppState }
>(ANALYSIS.SAVE_INDEPENDENT_CHARACTERISTICS_CONSTANT, async (_, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    return await contextModelAPI.saveIndependentCharacteristicsConstantForContextModel()
  }
  return null
})

export const saveDescriptiveCharacteristicsForContextModel = createAsyncThunk<
  any,
  { contextModel: ContextModel; data: DescriptiveCharacteristicPost[] },
  { state: AppState }
>(ANALYSIS.SAVE_DESCRIPTIVE_CHARACTERISTICS, async ({ contextModel, data }, { getState }) => {
  const {
    currentUser: { token },
  } = getState()
  if (token) {
    return await contextModelAPI.saveDescriptiveCharacteristicsForContextModel(contextModel, data)
  }
  return null
})

export const setRunsForETRLKPIs = createAsyncThunk<any, void, { state: AppState }>(
  ANALYSIS.SET_RUNS_FOR_ETRL_KPIS,
  async (_, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      const data = await etrlAPI.getRunsForETRLKpis()
      return data
    }
    return null
  },
)

export const setKPIsForCurrentRun = createAsyncThunk<any, { runId: number }, { state: AppState }>(
  ANALYSIS.SET_KPIS_FOR_CURRENT_RUN,
  async ({ runId }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      const results = await etrlAPI.getKPIsForCurrentRun(runId)
      return {
        runId,
        results,
      }
    }
    return null
  },
)

export const setOFAValues = createAsyncThunk<
  any,
  { contextModelVariantId: number; runId: number; module: string },
  { state: AppState }
>(ANALYSIS.SET_OFA_VALUES, async ({ contextModelVariantId, runId, module }, { getState }) => {
  const {
    currentUser: { token },
    currentAnalysis: { ofaTrainingCost },
  } = getState()
  if (token) {
    let data = await ofaAPI.getOFAValues(contextModelVariantId, runId)
    let beData = null
    if (module === Module.TRAINING) {
      beData = [...data]
      data = data.map((d) => ({
        ...d,
        additionalCost:
          d.additionalCost !== null
            ? `${Number(Number(d.additionalCost) * ofaTrainingCost).toFixed(2)}`
            : null,
        costAsPercentageOfPayroll:
          d.additionalCost !== null
            ? `${Number(
                (Number(d.additionalCost) * ofaTrainingCost * 100) /
                  Number(d.costAsPercentageOfPayroll),
              ).toFixed(3)}`
            : null,
      }))
    }
    return {
      contextModelVariantId,
      data,
      beData
    }
  }
  return null
})

export const setOFAOverall = createAsyncThunk<
  any,
  { contextModelVariantId: number },
  { state: AppState }
>(ANALYSIS.SET_OFA_OVERALL, async ({ contextModelVariantId }, { getState }) => {
  const {
    currentUser: { token },
    currentAnalysis: { ofaValues },
  } = getState()
  if (token && ofaValues && ofaValues[contextModelVariantId]) {
    const data = await ofaAPI.getOFAOverall(ofaValues[contextModelVariantId])
    return {
      contextModelVariantId,
      data,
    }
  }
  return null
})

export const setCWIMappings = createAsyncThunk<any, { runId: number }, { state: AppState }>(
  ANALYSIS.SET_CWI_MAPPINGS,
  async ({ runId }, { getState }) => {
    const {
      currentUser: { token },
    } = getState()
    if (token) {
      const data = await runAPI.getCWIOccupations(runId)
      return { runId, data }
    }
    return null
  },
)
